import { useContext, useMemo } from 'react'

import {
  digObject, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useRealtimeSubscription, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { FinanceApplicationModel, FinanceApplicationRequestOptions } from '@models/types'

import * as financeApplicationActions from '@redux/modules/financeApplication'
import type { ModuleState } from '@redux/modules/types'
import { AppDispatch } from '@redux/store'

import RealtimeContext from '@contexts/realtimeContext'

import useCurrentUser from './useCurrentUser'

const watchEntityKeys = ['financeApplications']

type CreateOrUpdateFinanceApplicationParams = {
  data: { payload: FinanceApplicationModel },
  dispatch: AppDispatch,
}

const createOrUpdateFinanceApplication = (params: CreateOrUpdateFinanceApplicationParams) => {
  const { data: { payload }, dispatch } = params
  const { approved, data: financeAppData } = payload

  const { createOrUpdateFinanceApplicationKey: createOrUpdateFn } = financeApplicationActions

  const declined = digObject(financeAppData, 'declined', false)

  const key = declined ? 'data' : 'approved'
  const value = declined ? { declined } : approved

  return dispatch(createOrUpdateFn({
    // @ts-expect-error TS2322
    incomingFinanceApplication: payload, key, value,
  }))
}

type FinanceApplicationFilters = {
  subject_id?: number,
  subject_type?: string,
}

type UseFinanceApplicationsOptions = {
  filters?: FinanceApplicationFilters,
  performHttpRequests?: boolean,
  requestOptions?: FinanceApplicationRequestOptions,
}

function useFinanceApplications(options: UseFinanceApplicationsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    subject_id: filterSubjectId,
    subject_type: filterSubjectType,
  } = filters

  const {
    updatedEntities: { financeApplications: financeApplicationsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { financeApplications } = useSelector(reduxState => reduxState.entities)

  const realtimeContext = useContext(RealtimeContext)
  // @ts-expect-error TS2339
  const { clientRef } = realtimeContext

  const { currentUser } = useCurrentUser()

  const dispatch = useDispatch()

  // Realtime/Websocket Connection
  useRealtimeSubscription({
    apiKey: process.env.REACT_APP_ABLY_PUBLIC_KEY,
    callbacks: {
      success: (data: { payload: FinanceApplicationModel }) => createOrUpdateFinanceApplication({ data, dispatch }),
    },
    channelName: 'financeApplications',
    clientRef,
    eventName: 'financeApplication.createdOrUpdated.complete',
  }, [currentUser.id])

  const filteredFinanceApplications = useMemo(() => {
    const filtered = Object.values(financeApplications).filter((financeApplication: FinanceApplicationModel) => {
      const {
        subject_id, subject_type,
      } = financeApplication

      const matchSubjectId = matchFilterNumber(subject_id, filterSubjectId)
      const matchSubjectType = matchFilterString(subject_type, filterSubjectType, { exact: true })

      return matchSubjectId && matchSubjectType
    })

    return sortArrayBy(filtered, 'asc')
  }, [financeApplicationsUpdatedAt, JSON.stringify(filters)])

  const filteredFinanceApplicationsCount = filteredFinanceApplications.length
  const hasFilteredFinanceApplications = !!filteredFinanceApplicationsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredFinanceApplicationsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingFinanceApplications } = useReduxAction(
    'financeApplications',
    'loadFinanceApplications',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredFinanceApplications,
    filteredFinanceApplicationsCount,
    hasFilteredFinanceApplications,
    loading: loadingFinanceApplications,
  }
}

export default useFinanceApplications
